import React, { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useLocation, useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabaseClient';
import toast from 'react-hot-toast';
import PasswordRules from './components/PasswordRules';
import { mapSupabaseErrorMessage } from './authErrors';
import AnimatedBackground from '../../components/AnimatedBackground';
import Logo from '../../components/Logo';
import { Eye, EyeOff } from 'lucide-react';

const newPasswordSchema = z.object({
  password: z
    .string()
    .min(8, 'Password must be at least 8 characters')
    .refine((val) => /[A-Z]/.test(val), 'Password must contain at least one uppercase letter')
    .refine((val) => /\d/.test(val), 'Password must contain at least one number')
    .refine((val) => /[^A-Za-z0-9]/.test(val), 'Password must contain at least one special character'),
  confirmPassword: z
    .string()
    .min(1, 'Please confirm your password'),
}).refine((data) => data.password === data.confirmPassword, {
  message: 'Passwords do not match',
  path: ['confirmPassword'],
});

type NewPasswordFormData = z.infer<typeof newPasswordSchema>;

const NewPasswordPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation() as any;
  const email = location?.state?.email as string | undefined;
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<NewPasswordFormData>({
    resolver: zodResolver(newPasswordSchema),
    defaultValues: {
      password: '',
      confirmPassword: '',
    },
  });

  const password = watch('password');
  const confirmPassword = watch('confirmPassword');

  const rules = useMemo(() => ({
    length: password ? password.length >= 8 : false,
    uppercase: password ? /[A-Z]/.test(password) : false,
    number: password ? /\d/.test(password) : false,
    special: password ? /[^A-Za-z0-9]/.test(password) : false,
    match: confirmPassword ? password === confirmPassword : false,
  }), [password, confirmPassword]);

  const onSubmit = async (data: NewPasswordFormData) => {
    // Require a valid recovery session set by verifyOtp
    const { data: sessionData } = await supabase.auth.getSession();
    if (!sessionData?.session) {
      toast.error('No valid reset session. Restart reset process.');
      return;
    }

    const { error } = await supabase.auth.updateUser({ password: data.password });
    if (error) {
      toast.error(mapSupabaseErrorMessage(error.message));
      return;
    }

    toast.success('Password updated');
    navigate('/login');
  };

  return (
    <div className="eg-auth-page auth-page">
      <AnimatedBackground />
      <div className="relative z-10 w-full max-w-2xl flex flex-col items-center">
        <div className="mb-4 pb-8 w-full">
          <Logo />
        </div>
        <div className="eg-auth-card">
          <div className="text-center mb-8">
            <h2 className="text-2xl font-light tracking-wide" style={{ color: 'var(--color-auth-text)' }}>Set New Password</h2>
          </div>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                placeholder="New password"
                {...register('password')}
                className={`eg-auth-input pr-10 ${errors.password ? 'input-error' : ''}`}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 transition-colors focus:outline-none"
                style={{ color: 'var(--color-auth-eye-icon)' }}
                onMouseEnter={(e) => e.currentTarget.style.color = 'var(--color-auth-eye-icon-hover)'}
                onMouseLeave={(e) => e.currentTarget.style.color = 'var(--color-auth-eye-icon)'}
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
            {errors.password && (
              <p className="text-sm text-red-400">{errors.password.message}</p>
            )}
            <div className="relative">
              <input
                type={showConfirmPassword ? "text" : "password"}
                placeholder="Confirm new password"
                {...register('confirmPassword')}
                className={`eg-auth-input pr-10 ${errors.confirmPassword ? 'input-error' : ''}`}
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 transition-colors focus:outline-none"
                style={{ color: 'var(--color-auth-eye-icon)' }}
                onMouseEnter={(e) => e.currentTarget.style.color = 'var(--color-auth-eye-icon-hover)'}
                onMouseLeave={(e) => e.currentTarget.style.color = 'var(--color-auth-eye-icon)'}
              >
                {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
            {errors.confirmPassword && (
              <p className="text-sm text-red-400">{errors.confirmPassword.message}</p>
            )}
            <PasswordRules rules={rules} />
            <button
              type="submit"
              disabled={isSubmitting}
              className="eg-auth-btn"
            >
              {isSubmitting ? 'Updating…' : 'Update password'}
            </button>
          </form>
          <div className="mt-4 text-xs text-center" style={{ color: 'var(--color-auth-text-muted)' }}>
            <p>Need help?  Contact <a href="mailto:support@engramiq.com" className="underline hover:text-[#17c480] transition-colors">support@engramiq.com</a> for assistance</p>
          </div>
        </div>
      </div>
      {/* <SupportChat /> */}
    </div>
  );
};

export default NewPasswordPage;
